import React, { useState, useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { getLocalTimeZone, today } from '@internationalized/date'
import { Button } from 'blaze-ui/Button'
import { ChevronDown2 } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { Select } from 'components/Select/Select'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Menu } from 'components/Menu/Menu'
import { Item } from '@react-stately/collections'
import { WeekDay } from 'types'
import {
  WEEK_DAYS,
  ONLY_WEEK_DAYS,
  ONLY_WEEKENDS,
} from 'screens/VirtualMarketerOnboarding/CustomizeSchedule/CustomizeSchedule'

import Styles from './CampaignDatePicker.module.scss'

interface CampaignDatePickerProps {
  startDate: string
  length: number
  weekDays: WeekDay[]
  onDateChange: (startDate: string, endDate: string, length: number) => void
  onWeekDaysChange: (weekDays: WeekDay[]) => void
  minDate?: string
  renderTrigger?: (formattedRange: string) => React.ReactNode
}

const LENGTH_OPTIONS = [
  { key: 1, label: '1 week' },
  { key: 2, label: '2 weeks' },
  { key: 3, label: '3 weeks' },
  { key: 4, label: '4 weeks' },
]

function formatDaysLabel(days: WeekDay[]): string {
  if (days.length < 1) return 'Select days'
  if (days.length === WEEK_DAYS.length) return 'Everyday'
  if (days.length === ONLY_WEEK_DAYS.length && ONLY_WEEK_DAYS.every((d) => days.includes(d))) return 'Weekdays'
  if (days.length === ONLY_WEEKENDS.length && ONLY_WEEKENDS.every((d) => days.includes(d))) return 'Weekends'
  return 'Custom'
}

export const CampaignDatePicker = ({
  startDate,
  length,
  weekDays,
  onDateChange,
  onWeekDaysChange,
  minDate,
  renderTrigger,
}: CampaignDatePickerProps) => {
  const [localStartDate, setLocalStartDate] = useState(startDate)
  const [localLength, setLocalLength] = useState(length)
  const [localWeekDays, setLocalWeekDays] = useState<WeekDay[]>(weekDays)

  const computeEndDate = (start: string, weeks: number) =>
    dayjs(start)
      .add(weeks * 7 - 1, 'day')
      .format('YYYY-MM-DD')

  const endDate = computeEndDate(localStartDate, localLength)

  const handleDateChange = useCallback(
    (date: ReturnType<typeof dayjs> | undefined) => {
      if (!date) return
      const newStart = date.format('YYYY-MM-DD')
      setLocalStartDate(newStart)
      onDateChange(newStart, computeEndDate(newStart, localLength), localLength)
    },
    [localLength, onDateChange],
  )

  const handleLengthChange = useCallback(
    (key: React.Key) => {
      const newLength = Number(key)
      setLocalLength(newLength)
      onDateChange(localStartDate, computeEndDate(localStartDate, newLength), newLength)
    },
    [localStartDate, onDateChange],
  )

  const handleDayToggle = useCallback(
    (key: React.Key) => {
      const day = key as WeekDay
      const newDays = new Set(localWeekDays)
      if (newDays.has(day)) {
        newDays.delete(day)
      } else {
        newDays.add(day)
      }
      const updated = [...newDays]
      setLocalWeekDays(updated)
      onWeekDaysChange(updated)
    },
    [localWeekDays, onWeekDaysChange],
  )

  const disableBefore = minDate ?? today(getLocalTimeZone()).add({ days: 1 }).toString()

  const formattedRange = `${dayjs(localStartDate).format('ddd, MMM D')} \u2013 ${dayjs(endDate).format('ddd, MMM D')}`

  const daysLabel = useMemo(() => formatDaysLabel(localWeekDays), [localWeekDays])

  return (
    <DateTimePicker
      disableDatesBefore={disableBefore}
      dateTime={dayjs(localStartDate)}
      setDateTime={handleDateChange}
      showTimeControls={false}
      showActions
      useSqueeze={false}
      popoverClassName={Styles.popover}
      wrapperClassName={Styles.wrapper}
      actions={(close) => (
        <div className={Styles.actions}>
          <div className={Styles.fieldRow}>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Start date</span>
              <Text variant="secondary" color="var(--dark-80)">
                {dayjs(localStartDate).format('ddd, MMM D YYYY')}
              </Text>
            </div>
            <div className={Styles.field}>
              <span className={Styles.fieldLabel}>Length</span>
              <Select
                selectedKey={String(localLength)}
                variant="secondary"
                size="md"
                onSelectionChange={handleLengthChange}
                aria-label="Campaign length"
              >
                {LENGTH_OPTIONS.map((opt) => (
                  <Item key={opt.key} textValue={opt.label}>
                    <Text variant="primary" color="inherit">
                      {opt.label}
                    </Text>
                  </Item>
                ))}
              </Select>
            </div>
          </div>
          <div className={Styles.field}>
            <span className={Styles.fieldLabel}>Days to post (per channel)</span>
            <DropdownTrigger placement="bottom-start">
              <Button variant="secondary" size="md" aria-label="Select days to post">
                {daysLabel}
              </Button>
              <Menu
                onAction={handleDayToggle}
                selectionMode="multiple"
                selectedKeys={localWeekDays}
                showCheckmark
                aria-label="Select days to post"
              >
                {WEEK_DAYS.map((day) => (
                  <Item key={day} textValue={day}>
                    {day}
                  </Item>
                ))}
              </Menu>
            </DropdownTrigger>
          </div>
          <Button variant="primary" size="md" onPress={() => close()} fullWidth>
            Save Changes
          </Button>
        </div>
      )}
    >
      {renderTrigger ? (
        renderTrigger(formattedRange)
      ) : (
        <Button variant="secondary" size="md" endIcon={ChevronDown2} fullWidth>
          <Text variant="secondary" color="var(--dark-80)">
            {formattedRange}
          </Text>
        </Button>
      )}
    </DateTimePicker>
  )
}
